import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import "./timeline.css"

import React, { useState, useMemo, useCallback, useLayoutEffect, useEffect, Suspense } from 'react';

import { toast } from 'react-toastify';

import { DateSelectArg, EventChangeArg } from '@fullcalendar/react'; // must go before plugins
import { dateOnly, formatToYMD } from '../../helpers/dateHelper';
import { PromotionCriteria, promotionCriteriaToQuery } from '../../backoffice/dtos/promotions/promotionCriteria';
import {
    useAddPromotionMutation, useAlterPromotionProductMutation,
    useClonePromotionMutation
} from '../../backoffice/apis/promotionAPI';
import { Promotion } from '../../backoffice/dtos/promotions/promotion';
import CriteriaModal from '../criteria/CriteriaModal';
import PromotionsTimeline from "./PromotionsTimeline";
import { TimelineEventClick } from '../../app/types';
import { useGetTimelineResourcesQuery } from "../../backoffice/apis/timelineAPI";
import { PromotionPartialChange, getDefaultTimelineRangeStr } from '../../helpers/Types';
import { Modal, ModalBody, Spinner } from 'reactstrap';
import StatisticsBody from "../statistics/StatisticsBody";
import { useSetPromotionId } from '../../app/hooks';
import { useTranslation } from 'react-i18next';
import { useGetMonthsHolidaysQuery } from '../../backoffice/apis/holidaysAPI';
import { RouteProps } from 'react-router-dom';
import { Loading } from '../views/Loading';
import { PromotionColorInfo } from './PromotionColorInfo';
import { ResourceItem, TimelineMasterWithEvent, toResourceItems, toTimelineEvents } from '../../backoffice/dtos/timeline/timelineModel';
import StatisticsModal from '../statistics/StatisticsModal';
import { unwrapError } from '../../backoffice/apis/axios';
import { getMessageFromRtkError } from '../../backoffice/apis/baseAPI';

interface PromotionsTimelineBaseArgs extends React.HTMLAttributes<RouteProps> {
}

export const TimelineDefaultPromotionCriteria: Readonly<PromotionCriteria> = {
    customerIds: undefined, approvalStatus: undefined, mediaTypeIds: undefined, dateRange: getDefaultTimelineRangeStr()
}

export default function PromotionsTimelineBase(props: PromotionsTimelineBaseArgs) {
    const [promotionCriteria, setPromotionCriteria] = useState<PromotionCriteria>(TimelineDefaultPromotionCriteria)

    const { t } = useTranslation(['promotion_timeline', 'promotion', 'common'])
    //
    // RTK Query api
    //
    const { data: holidays } = useGetMonthsHolidaysQuery(promotionCriteria.dateRange!);
    const { data: timelineResources, isFetching } = useGetTimelineResourcesQuery(promotionCriteria, {
        refetchOnMountOrArgChange: true
    });

    const [addPromotion] = useAddPromotionMutation()
    const [clonePromotion] = useClonePromotionMutation()
    const [alterPromotion] = useAlterPromotionProductMutation()

    const setPromotionId = useSetPromotionId()

    const [isColorInfoOpen, setColorInfoOpen] = useState(false)

    const toggleColorInfo = useCallback(() => setColorInfoOpen(ref => !ref), [setColorInfoOpen]);

    const [focusedDate, setFocusedDate] = useState<Date>()

    //
    // End of RTK Query api
    //
    const eventDateRangeChange = useCallback(async (e: EventChangeArg) => {
        const masterWithEvent = e.event.extendedProps as TimelineMasterWithEvent;
        const targetResource = e.event.getResources()[0]._resource.extendedProps

        if (!targetResource)
            return;

        const [newStart, newEnd] = [e.event._instance!.range.start, e.event._instance!.range.end]

        let dateRange = null;
        if (newStart && newEnd) {
            newEnd.setDate(newEnd.getDate() - 1)
            dateRange = { fromDate: formatToYMD(newStart), toDate: formatToYMD(newEnd) };
        }

        let mediaTypeId = null;
        if (targetResource.mediaTypeId !== masterWithEvent.mediaType.mediaTypeId)
            mediaTypeId = targetResource.mediaTypeId ?? null;

        let body: PromotionPartialChange | null = null;
        if (dateRange || mediaTypeId)
            body = { mediaTypeId, dateRange }

        let focusedDate = new Date(masterWithEvent.event.fromDate);
        if (dateRange)
            focusedDate = new Date(dateRange.fromDate);

        setFocusedDate(focusedDate);

        try {
            if (targetResource.customerId !== masterWithEvent.customer.customerId || targetResource.mediaTypeId !== masterWithEvent.mediaType.mediaTypeId) {
                await clonePromotion({ id: masterWithEvent.event.id, newCustomerId: targetResource.customerId, body }).unwrap()
            } else if (body) {
                await alterPromotion({ id: masterWithEvent.event.id, body }).unwrap()
            }
        } catch (e: any) {
            toast(e);
        }
    }, [clonePromotion, alterPromotion]);

    const [showFilterModal, setShowFilterModal] = useState(false);

    const createEventForDateRange = useCallback(async (arg: DateSelectArg) => {
        const masterWithEvent = arg.resource?._resource.extendedProps as ResourceItem

        if (masterWithEvent === undefined)
            return;

        const customerId = masterWithEvent.customerId;
        const mediaTypeId = masterWithEvent.mediaTypeId;
        const mediaTypeDescription = masterWithEvent.mediaTypeDescription;
        const groupId = masterWithEvent.groupId;
        const groupDescription = masterWithEvent.groupDescription;

        // end date is EXCLUSIVE
        let toDate = dateOnly(arg.end)
        toDate.setDate(toDate.getDate() - 1)

        const fromDate = dateOnly(arg.start)

        if (fromDate >= toDate)
            return

        const newPromotion: Partial<Promotion> = {
            customerId: customerId,
            fromDate: formatToYMD(arg.start),
            toDate: formatToYMD(toDate),
            mediaTypeId: mediaTypeId,
            mediaTypeDescription: mediaTypeDescription,
            groupId: groupId,
            groupDescription: groupDescription,
            description: undefined
        }

        try {
            setFocusedDate(fromDate);

            const payload = await addPromotion(newPromotion).unwrap();
            setPromotionId(payload.id);
        } catch (e: any) {
            toast(t('unsuccessful_promotion_creation_text', { ns: 'promotion', error: getMessageFromRtkError(e) }))
        }
    }, [addPromotion])

    const promotionOpenEvent = useCallback((info: TimelineEventClick) => {
        const eventWithMaster: TimelineMasterWithEvent = info.event.extendedProps;
        const promotionId = eventWithMaster.event.id;

        setFocusedDate(new Date(eventWithMaster.event.fromDate))
        setPromotionId(promotionId)
    }, [])

    const resources = useMemo(() => {
        // debugger;
        return toResourceItems(timelineResources);
    }, [timelineResources]);

    const timelineEvents = useMemo(() => {
        return toTimelineEvents(timelineResources);
    }, [timelineResources]);

    /*
     * END MODAL SECTION
     */
    const [showStatistics, setShowStatistics] = useState(false)
    let mainView: JSX.Element
    if (isFetching) {
        mainView = (
            <Loading />
        )
    } else {
        mainView = (
            <PromotionsTimeline
                focusedDate={focusedDate}
                holidays={holidays}
                promotionCriteria={promotionCriteria}
                toggleStatistics={() => setShowStatistics(val => !val)}
                toggleShowFilters={() => setShowFilterModal(prev => !prev)}
                timelineEvents={timelineEvents}
                timelineResources={resources}
                eventClickHandler={promotionOpenEvent}
                calendarSelectHandler={createEventForDateRange}
                eventDateRangeChange={eventDateRangeChange}
                setPromotionCriteria={(promotionCriteria: PromotionCriteria) => setPromotionCriteria(promotionCriteria)}
                toggleColorInfo={toggleColorInfo}
            />
        )
    }

    return <>
        {mainView}

        <StatisticsModal
            promotionCriteria={promotionCriteria}
            showStatistics={showStatistics}
            setShowStatistics={setShowStatistics}
            setPromotionCriteria={setPromotionCriteria}
        />

        <CriteriaModal
            isOpen={showFilterModal}
            toggle={() => {
                setShowFilterModal(prev => !prev)
            }}
            currentFilter={promotionCriteria}
            setSelectedPromotionCriteria={(criteria) => {
                setPromotionCriteria(criteria)
            }}
            initialFilter={TimelineDefaultPromotionCriteria}
        />

        <PromotionColorInfo isOpen={isColorInfoOpen} toggle={toggleColorInfo} />
    </>
}
